// Tier 4 — Investment / Buy-vs-Rent engine.
// Pure functions. Consumes Tier 1 (affordability), Tier 2 (loan schedule)
// and Tier 3 (market CAGR) outputs only — no second state layer.
//
// Property value is NEVER treated as investable capital.
// "Rent" alternate scenario only invests:
//   1. The down payment
//   2. The monthly surplus between EMI and (rent + maintenance)
//   3. The 6-month rental deposit (locked, 0% appreciation)

function sipFvMonth(monthlyInvest, monthlyRate) {
    return monthlyInvest * (1 + monthlyRate);
}

// ── Tier 4 · Investment composite ──────────────────────────────────
export function computeInvestmentOutputs(inputs) {
    const {
        downPayment = 0,
        emi = 0,
        tenure = 20,
        eligible_loan = 0,
        property_value_t0 = 0,
        schedule = [],
        rent = null,
        sba = 1500,
        propertyCAGR = 6.5,
        horizon = 10,
        marketChoice = null,          // { source: 'pinned'|'best_within'|'fallback', market: {...}|null }
        affordabilitySnapshot = null, // { foirUsagePct, downRatio, segment }
        assumptions = {},
    } = inputs || {};

    const {
        equityCAGR = 12,            // %
        rentEscalationPct = 5,      // %
        maintPerSqftMo = 6,         // ₹/sqft/mo
        maintGstPct = 18,           // % GST
        maintEscalationPct = 5,     // % maintenance steps up annually
        depositMonths = 6,
    } = assumptions;

    // ── Blocked state — rent required.
    if (rent == null || rent <= 0) {
        return {
            blocked: true,
            blockedReason: "rent_required",
            ladder: [],
            horizonSnapshot: null,
            buyVsRentBreakEvenYear: null,
            costOfWaiting: { 1: 0, 2: 0, 3: 0, 5: 0 },
            recommendation: blockedRecommendation(),
            investment_score: null,
            marketChoice,
            inputsEcho: { downPayment, emi, tenure, propertyCAGR, sba, horizon, ...assumptions },
        };
    }

    const eqM = (equityCAGR / 100) / 12;
    const propG = propertyCAGR / 100;
    const rentG = rentEscalationPct / 100;
    const maintG = maintEscalationPct / 100;
    const maintMonthlyT0 = sba * maintPerSqftMo * (1 + maintGstPct / 100); // ₹/mo, year 1 GST-inclusive
    const deposit = depositMonths * rent;

    // year → cumulative_principal map
    const cumPrincipalByYear = new Map();
    {
        let cum = 0;
        for (const row of schedule) {
            cum += row.principal;
            cumPrincipalByYear.set(row.year, cum);
        }
    }
    const outstandingLoanAtYear = (year) => {
        if (year <= 0) return eligible_loan;
        if (year >= tenure) return 0;
        const cum = cumPrincipalByYear.get(year) ?? 0;
        return Math.max(0, eligible_loan - cum);
    };

    // ── Per-year ladder ────────────────────────────────────────────
    // Always project up to 20 yrs (or horizon if larger) so the UI can show
    // milestone checkpoints at 5/10/15/20 even when horizon < 20.
    const projectionYears = Math.max(20, horizon);
    const ladder = [];
    let renterSipCorpus = 0;
    let cumulativeRent = 0;
    let cumulativeMaint = 0;
    for (let y = 1; y <= projectionYears; y++) {
        const yearRent = rent * Math.pow(1 + rentG, y - 1);             // escalates 5%/yr
        const maintMonthly = maintMonthlyT0 * Math.pow(1 + maintG, y - 1); // escalates 5%/yr
        for (let m = 1; m <= 12; m++) {
            renterSipCorpus = sipFvMonth(renterSipCorpus, eqM);
            const tenantOut = yearRent + maintMonthly;
            const surplus = Math.max(0, emi - tenantOut);
            renterSipCorpus += surplus;
            cumulativeRent += yearRent;
            cumulativeMaint += maintMonthly;
        }
        const dpInvestedFV = downPayment * Math.pow(1 + equityCAGR / 100, y);
        const propertyValue = property_value_t0 * Math.pow(1 + propG, y);
        const outstanding = outstandingLoanAtYear(y);
        const equityBuilt = eligible_loan - outstanding;
        const netBuyWorth = propertyValue - outstanding;
        const netRentWorth = dpInvestedFV + renterSipCorpus + deposit;
        ladder.push({
            year: y,
            propertyValue, outstandingLoan: outstanding, equityBuilt, netBuyWorth,
            dpInvestedFV, sipCorpus: renterSipCorpus, deposit,
            cumulativeRent, cumulativeMaint, netRentWorth,
            advantage: netBuyWorth - netRentWorth,
        });
    }

    // ── Decision metrics ───────────────────────────────────────────
    const breakEvenEntry = ladder.find((row) => row.netBuyWorth >= row.netRentWorth);
    const buyVsRentBreakEvenYear = breakEvenEntry ? breakEvenEntry.year : null;

    const costOfWaiting = {};
    [1, 2, 3, 5].forEach((y) => {
        costOfWaiting[y] = property_value_t0 * Math.pow(1 + propG, y) - property_value_t0;
    });

    const horizonSnapshot = ladder[horizon - 1] || ladder[ladder.length - 1] || null;
    const recommendation = composeRecommendation({
        horizonSnapshot,
        marketChoice,
        propertyCAGR,
        affordabilitySnapshot,
        rent, emi, deposit,
    });
    const investment_score = computeInvestmentScore(recommendation);

    return {
        blocked: false,
        blockedReason: null,
        ladder,
        horizonSnapshot,
        buyVsRentBreakEvenYear,
        costOfWaiting,
        recommendation,
        investment_score,
        marketChoice,
        // Echoes
        maintenanceMonthlyT0: maintMonthlyT0,
        deposit,
        propertyCAGR,
        equityCAGR,
        horizon,
        inputsEcho: { downPayment, emi, tenure, propertyCAGR, sba, rent, horizon, ...assumptions },
    };
}

// ── Recommendation engine — 3 pillars + composite ──────────────────
function composeRecommendation({
    horizonSnapshot, marketChoice, propertyCAGR, affordabilitySnapshot, rent, emi, deposit,
}) {
    // ── Pillar 1 · Financial Outcome ────────────────────────────────
    let financial = { verdict: "Marginal", confidence: "Low", marginPct: 0, summary: "" };
    if (horizonSnapshot && horizonSnapshot.netRentWorth > 0) {
        const margin = (horizonSnapshot.netBuyWorth - horizonSnapshot.netRentWorth) / horizonSnapshot.netRentWorth;
        financial.marginPct = margin * 100;
        if (margin >= 0.10) financial.verdict = "Buy";
        else if (margin <= -0.10) financial.verdict = "Rent";
        const absM = Math.abs(margin);
        if (absM >= 0.25) financial.confidence = "High";
        else if (absM >= 0.10) financial.confidence = "Moderate";
        if (financial.verdict === "Buy") {
            financial.summary = `Buying leaves you ${formatPct(margin * 100)} wealthier at the horizon — owning is the better wealth move.`;
        } else if (financial.verdict === "Rent") {
            financial.summary = `Renting and investing the surplus leaves you ${formatPct(Math.abs(margin) * 100)} wealthier — your money works harder elsewhere.`;
        } else {
            financial.summary = "Both paths land within 10% of each other — financial outcome is a tie.";
        }
    } else {
        financial.summary = "Not enough data to compare net worth scenarios.";
    }

    // ── Pillar 2 · Market Opportunity ───────────────────────────────
    let market = { verdict: "Weak", summary: "", source: marketChoice?.source ?? null, area: marketChoice?.market?.area ?? null };
    const status = marketChoice?.market?.status;
    if (status === "Within" && propertyCAGR >= 7) market.verdict = "Strong";
    else if (status === "Within" && propertyCAGR >= 5) market.verdict = "Solid";
    else if (status === "Stretch" && propertyCAGR >= 7) market.verdict = "Solid";
    else market.verdict = "Weak";

    if (marketChoice?.source === "fallback") {
        market.summary = "No market selected — using a conservative 6.5% benchmark CAGR. Pin a market in the Market tab for tailored insight.";
        market.verdict = "Weak";
    } else if (market.verdict === "Strong") {
        market.summary = `${market.area} is within budget AND projects ${propertyCAGR.toFixed(1)}% CAGR — entering now compounds aggressively.`;
    } else if (market.verdict === "Solid") {
        const tag = status === "Within" ? "within budget" : "a small stretch";
        market.summary = `${market.area} is ${tag} with steady ${propertyCAGR.toFixed(1)}% CAGR — a reasonable entry.`;
    } else {
        market.summary = `${market.area ?? "Selected market"} growth (${propertyCAGR.toFixed(1)}% CAGR) is muted — wealth creation lever is weaker here.`;
    }

    // ── Pillar 3 · Lifestyle Context ────────────────────────────────
    // Surfaces three explicit metrics so the UI can render them as
    // standalone insight tiles (FOIR · DP Cushion · Cashflow Shock).
    let lifestyle = { verdict: "Comfortable", summary: "", metrics: {} };
    const foirUsage = affordabilitySnapshot?.foirUsagePct ?? 0;
    const downRatio = affordabilitySnapshot?.downRatio ?? 0;
    const cashflowShock = rent > 0 ? (emi - rent) / rent : 0;

    const foirStress = foirUsage >= 50;
    const dpStress = downRatio < 0.15;
    const cashShock = cashflowShock >= 1.0;
    const stresses = [foirStress, dpStress, cashShock].filter(Boolean).length;
    if (stresses === 0) lifestyle.verdict = "Comfortable";
    else if (stresses === 1) lifestyle.verdict = "Stretch";
    else lifestyle.verdict = "Tight";

    // FOIR tile: Healthy < 40% · Stretch 40-50% · Tight ≥ 50%
    const foirStatus = foirUsage >= 50 ? "Tight" : foirUsage >= 40 ? "Stretch" : "Healthy";
    // DP Cushion tile: Healthy ≥ 20% · Stretch 15-20% · Tight < 15%
    const dpStatus = downRatio < 0.15 ? "Tight" : downRatio < 0.20 ? "Stretch" : "Healthy";
    // Cashflow Shock tile: Healthy < 30% · Stretch 30-100% · Tight ≥ 100%
    const cashStatus = cashflowShock >= 1.0 ? "Tight" : cashflowShock >= 0.3 ? "Stretch" : "Healthy";

    lifestyle.metrics = {
        foir: {
            label: "FOIR",
            valuePct: foirUsage,
            status: foirStatus,
            caption: foirStatus === "Healthy"
                ? "EMI fits comfortably inside your income."
                : foirStatus === "Stretch"
                    ? "EMI is approaching the lender ceiling — limited headroom."
                    : "EMI eats half your income — refinancing room is small.",
        },
        downCushion: {
            label: "Down Payment Cushion",
            valuePct: downRatio * 100,
            status: dpStatus,
            caption: dpStatus === "Healthy"
                ? "Down payment ≥ 20% of loan — bank-friendly cushion."
                : dpStatus === "Stretch"
                    ? "Down payment 15–20% of loan — acceptable but tight."
                    : "Down payment under 15% — magnifies risk on a price dip.",
        },
        cashflowShock: {
            label: "Cashflow Shock",
            valuePct: cashflowShock * 100,
            status: cashStatus,
            caption: cashStatus === "Healthy"
                ? "EMI lands in the same ballpark as your current rent."
                : cashStatus === "Stretch"
                    ? "EMI is meaningfully higher than rent — adjust monthly budget."
                    : "EMI is more than double your rent — a real lifestyle shift.",
        },
    };

    const parts = [];
    parts.push(foirStatus !== "Healthy"
        ? `EMI eats ${foirUsage.toFixed(0)}% of salary — limited cushion`
        : `EMI uses ${foirUsage.toFixed(0)}% of salary — healthy headroom`);
    parts.push(dpStatus !== "Healthy"
        ? "down payment is under 20% of loan — bigger risk on dips"
        : "down payment cushion looks adequate");
    if (rent > 0) {
        if (cashStatus === "Tight") parts.push(`EMI is ${(cashflowShock * 100).toFixed(0)}% more than your current rent — meaningful monthly shock`);
        else if (cashStatus === "Stretch") parts.push(`EMI is ${(cashflowShock * 100).toFixed(0)}% more than rent — adjust budget for the step-up`);
        else parts.push("EMI is in the same ballpark as your current rent — no lifestyle shock");
    }
    lifestyle.summary = parts.join(" · ") + ".";

    // ── Composite (lightweight rules) ──────────────────────────────
    const score =
        (financial.verdict === "Buy" ? 2 : financial.verdict === "Rent" ? -2 : 0) *
            (financial.confidence === "High" ? 1.5 : financial.confidence === "Moderate" ? 1.2 : 1)
        + (market.verdict === "Strong" ? 2 : market.verdict === "Solid" ? 1 : -1)
        + (lifestyle.verdict === "Comfortable" ? 1 : lifestyle.verdict === "Stretch" ? 0 : -2);
    let composite = { verdict: "Marginal", confidence: "Low", score };
    if (score >= 3) composite.verdict = "Buy";
    else if (score >= 0.5) composite.verdict = "Considered Buy";
    else if (score <= -2) composite.verdict = "Rent";
    else composite.verdict = "Wait";
    composite.confidence = Math.abs(score) >= 4 ? "High" : Math.abs(score) >= 2 ? "Moderate" : "Low";

    return { financial, market, lifestyle, composite };
}

// ── S_inv — investment sub-score for Tier 7 composite ───────────────
//   Base 60
//   + Financial: marginPct clamped [-30, +50] mapped → [-15, +25]
//   + Market verdict   : Strong +12 · Solid +6 · Weak −8 · Pending 0
//   + Lifestyle verdict: Comfortable +8 · Stretch 0 · Tight −8
//   Cap [0, 100]
export function computeInvestmentScore(recommendation) {
    if (!recommendation) return null;
    const fin = recommendation.financial;
    const mkt = recommendation.market;
    const ls = recommendation.lifestyle;
    if (!fin || fin.verdict === "Pending") return null;

    const marginClamped = Math.max(-30, Math.min(50, fin.marginPct ?? 0));
    // Linear map: -30 → -15, +50 → +25 (range 80 → 40)
    const financialDelta = marginClamped >= 0
        ? (marginClamped / 50) * 25
        : (marginClamped / 30) * 15;

    const marketDelta = mkt?.verdict === "Strong" ? 12
        : mkt?.verdict === "Solid" ? 6
        : mkt?.verdict === "Weak" ? -8
        : 0;

    const lifestyleDelta = ls?.verdict === "Comfortable" ? 8
        : ls?.verdict === "Tight" ? -8
        : 0;

    return Math.max(0, Math.min(100, Math.round(60 + financialDelta + marketDelta + lifestyleDelta)));
}

function blockedRecommendation() {
    return {
        financial: { verdict: "Pending", confidence: "Low", marginPct: 0, summary: "Enter your current monthly rent to personalise the financial comparison." },
        market: { verdict: "Pending", summary: "Pending — needs rent to project." },
        lifestyle: { verdict: "Pending", summary: "Pending — needs rent to assess monthly shock." },
        composite: { verdict: "Pending", confidence: "Low", score: 0 },
    };
}

function formatPct(p) {
    return `${p.toFixed(1)}%`;
}
